import { useDispatch } from 'react-redux';
import {
	Col,
	Alert,
	Spinner,
	CardBody,
	Card,
	CardHeader,
	Row,
	Container,
	Button,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getFuelSupplySummary, getFuelSupplySummaryQuery } from '../../../slices/fuelSupply/thunk';
import UiContent from '../../../Components/Common/UiContent';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import OffcanvasContainer from './Modals/OffcanvasContainer';
import Section from './Section';

const FuelSupplySummary = () => {
	const dispatch = useDispatch();

	const breadcrumbLinks = [
		{ to: '#', label: 'Inicio', icon: 'icon-house' },
		{ to: '#', label: 'Abastecimiento', icon: 'icon-shopping-basket' },
		{ to: '#', label: 'Combustible', icon: 'icon-fuel' },
	];

	const [isRight, setIsRight] = useState(false);
	const [filters, setFilters] = useState({
		dateRange: [],
		establishment_id: null,
		establishment_name: null,
		establishment_type_id: null,
		establishment_type_name: null,
		group_by_artifacts_and_tanks: false,
		hide_detail_columns: false,
	});

	const toggleRightCanvas = useCallback(() => {
		setIsRight(prevIsRight => !prevIsRight);
	}, []);

	const handleFiltersSubmit = useCallback(newFilters => {
		setFilters(newFilters);
	}, []);

	const selectorFuelSupply = createSelector(
		state => state.FuelSupply,
		data => ({
			fuelSupplyList: data.itemFuelSupplyList,
			isLoadingList: data.isLoadingList,
			isFuelSupplySuccess: data.isFuelSupplySuccess,
		})
	);

	const { fuelSupplyList, isLoadingList } = useSelector(selectorFuelSupply);

	useEffect(() => {
		dispatch(getFuelSupplySummaryQuery(filters));
	}, [filters]);

	function formatNumber(value) {
		const fixedValue = Number(value).toFixed(1); // ejemplo: "308.84"
		const [integerPart, decimalPart] = fixedValue.split('.'); // Separar parte entera y decimal
		const formattedInteger = integerPart.replace(
			/\B(?=(\d{3})+(?!\d))/g,
			'.'
		); // Formatear parte entera con separadores de miles
		return `${formattedInteger},${decimalPart}`; // Combinar con parte decimal usando coma
	}

	const columns = useMemo(() => {
		const cols = [
			{
				header: 'Establecimiento',
				accessorKey: 'establishment_name',
				width: '1%',
				dataType: 'multi-select',
			},
		];

		if (filters.group_by_artifacts_and_tanks) {
			cols.push(
				{
					header: 'Artefacto Naval',
					accessorKey: 'feeding_barges_name',
					width: '1%',
					dataType: 'multi-select',
				},
				{
					header: 'Estanque',
					accessorKey: 'fuel_tank_name',
					width: '1%',
					dataType: 'multi-select',
				}
			);
		}

		cols.push(
			{
				header: 'Tipo',
				accessorKey: 'fuel_type_combined',
				width: '1%',
				dataType: 'multi-select',
				accessorFn: row =>
					`${row.fuel_type_name} (${row.unit_measurement_name})`,
				Cell: ({ row }) =>
					`${row.original.fuel_type_name} (${row.original.unit_measurement_name})`,
			},

			{
				header: 'Año',
				accessorFn: row =>
					typeof row.year === 'string'
						? row.year
						: row.year?.toString() ?? '',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Mes',
				accessorKey: 'month',
				width: '1%',
				dataType: 'multi-select',
			},
			{
				header: 'Saldo Incial',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'initial_balance',
				width: '1%',
				dataType: 'separator',
				summary: true,
				Cell: ({ row }) => {
					const value = row.original.initial_balance;
					if (value == null || value <= 0) return '-';
					return (
						<span className='badge bg-secondary'>
							{formatNumber(value)}
						</span>
					);
				},
			}
		);

		if (filters.hide_detail_columns == false) {
			cols.push(
				{
					header: 'Abastecimiento',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.supply',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.supply;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-success text-success'>
								{formatNumber(value)}
							</span>
						);
					},
				},
				{
					header: 'Recepción',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.input_transfer',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.input_transfer;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-success text-success'>
								{formatNumber(value)}
							</span>
						);
					},
				},
				{
					header: 'Trasvasije',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.transfer_in',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.transfer_in;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-success text-success'>
								{formatNumber(value)}
							</span>
						);
					},
				}
			);
		}

		cols.push({
			header: 'Entradas',
			enableSorting: false,
			enableColumnFilter: false,
			enableGlobalFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			enableColumnFilter: false,
			enableGlobalFilter: false,
			enableColumnActions: false,
			accessorKey: 'total_income',
			width: '1%',
			dataType: 'separator',
			summary: true,
			Cell: ({ row }) => {
				const value = row.original.total_income;
				if (value == null || value <= 0) return '-';
				return (
					<span className='badge bg-success'>{formatNumber(value)}</span>
				);
			},
		});

		if (filters.hide_detail_columns == false) {
			cols.push(
				{
					header: 'Consumo',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.consumption',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.consumption;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-danger text-danger'>
								{formatNumber(value)}
							</span>
						);
					},
				},
				{
					header: 'Merma',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.loss',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.loss;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-danger text-danger'>
								{formatNumber(value)}
							</span>
						);
					},
				},
				{
					header: 'Ajuste',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.patch',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.patch;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-danger text-danger'>
								{formatNumber(value)}
							</span>
						);
					},
				},
				{
					header: 'Trasvasije',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.transfer_out',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.transfer_out;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-danger text-danger'>
								{formatNumber(value)}
							</span>
						);
					},
				},
				{
					header: 'Traslado',
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					enableSorting: false,
					enableColumnFilter: false,
					enableGlobalFilter: false,
					enableColumnActions: false,
					accessorKey: 'types.output_transfer',
					width: '1%',
					dataType: 'separator',
					summary: true,
					Cell: ({ row }) => {
						const value = row.original.types?.output_transfer;
						if (value == null || value <= 0) return '-';
						return (
							<span className='badge border border-danger text-danger'>
								{formatNumber(value)}
							</span>
						);
					},
				}
			);
		}

		cols.push(
			{
				header: 'Salidas',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'total_output',
				width: '1%',
				dataType: 'separator',
				summary: true,
				Cell: ({ row }) => {
					const value = row.original.total_output;
					if (value == null || value <= 0) return '-';
					return (
						<span className='badge bg-danger'>{formatNumber(value)}</span>
					);
				},
			},
			{
				header: 'Saldo Final',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'final_balance',
				width: '1%',
				dataType: 'separator',
				summary: true,
				Cell: ({ row }) => {
					const value = row.original.final_balance;
					if (value == null || value <= 0) return '-';
					return (
						<span className='badge bg-primary'>
							{formatNumber(value)}
						</span>
					);
				},
			}
		);

		return cols;
	}, [filters.group_by_artifacts_and_tanks, filters.hide_detail_columns]);

	return (
		<React.Fragment>
			<UiContent />
			<div className='page-content'>
				<Container fluid>
					<BreadCrumb
						title='Control de Combustible'
						links={breadcrumbLinks}
						icon={'icon-circle-gauge'}
					/>

					<Row>
						<Col lg={12}>
							<Card>
								<CardHeader className='border-0 pb-0'>
									<Section
										filters={filters}
										rightCanvasBtn={toggleRightCanvas}
									/>

									<OffcanvasContainer
										isOpen={isRight}
										toggle={toggleRightCanvas}
										filters={filters}
										onSubmit={handleFiltersSubmit}
									/>
								</CardHeader>

								<CardBody>
									<ODataGrid
										key={`grid_fuel_${filters.group_by_artifacts_and_tanks}_${filters.hide_detail_columns}`}
										columns={columns}
										// data={fuelSupplyList || []}
										nameFile={'kardex_combustible'}
										// enableStickyFooter={true}
										getDataFunc={getFuelSupplySummaryQuery}
										remoteOperations={{
											pagination: true,
											sorting: true,
											filtering: true,
										}}
									/>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</div>
		</React.Fragment>
	);
};

export default FuelSupplySummary;
